import React, { useState, useEffect } from "react";
import {
  CurrencyDollarIcon,
  BanknotesIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import { userService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import toast from "react-hot-toast";

function EarningsPage() {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      paymentMethod: "paypal",
      amount: "",
      paypalEmail: user?.paypalEmail || "",
    },
  });

  const paymentMethod = watch("paymentMethod");
  const amount = watch("amount");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [earningsRes, paymentsRes] = await Promise.all([
        userService.getEarnings({ limit: 50 }),
        userService.getPayments({ limit: 10 }),
      ]);

      if (earningsRes.data.success) {
        setEarnings(earningsRes.data.data.earnings || []);
      }

      if (paymentsRes.data.success) {
        setPayments(paymentsRes.data.data.payments || []);
      }
    } catch (error) {
      toast.error("Veriler yüklenirken bir hata oluştu");
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitWithdrawal = async (data) => {
    try {
      const withdrawalData = {
        amount: parseFloat(data.amount),
        paymentMethod: data.paymentMethod,
        paymentDetails:
          data.paymentMethod === "paypal"
            ? { paypalEmail: data.paypalEmail }
            : {
                bankName: data.bankName,
                accountNumber: data.accountNumber,
                iban: data.iban,
              },
      };

      const response = await userService.requestWithdrawal(withdrawalData);

      if (response.data.success) {
        toast.success("Ödeme talebiniz başarıyla oluşturuldu");
        setShowWithdrawalForm(false);
        loadData();
      }
    } catch (error) {
      // Error handling in API service
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case "pending":
      case "processing":
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: "Beklemede",
      processing: "İşleniyor",
      completed: "Tamamlandı",
      failed: "Başarısız",
      cancelled: "İptal Edildi",
    };
    return statusMap[status] || status;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Kazanç verileri yükleniyor..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Kazançlarım</h1>
        <p className="text-gray-600">
          Kazanç geçmişinizi inceleyin ve ödeme talebinde bulunun
        </p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <div className="card-body text-center">
            <CurrencyDollarIcon className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-600">Mevcut Bakiye</p>
            <p className="text-3xl font-bold text-green-600">
              ${user?.availableBalance?.toFixed(2) || "0.00"}
            </p>
            <p className="text-xs text-gray-500 mt-1">Çekilebilir tutar</p>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <BanknotesIcon className="w-12 h-12 text-blue-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-600">Toplam Kazanç</p>
            <p className="text-3xl font-bold text-gray-900">
              ${user?.totalEarnings?.toFixed(2) || "0.00"}
            </p>
            <p className="text-xs text-gray-500 mt-1">Toplam kazandığınız</p>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <CalendarIcon className="w-12 h-12 text-purple-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-600">Çekilen Tutar</p>
            <p className="text-3xl font-bold text-gray-900">
              ${user?.totalWithdrawn?.toFixed(2) || "0.00"}
            </p>
            <p className="text-xs text-gray-500 mt-1">Daha önce çekilen</p>
          </div>
        </div>
      </div>

      {/* Withdrawal Section */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Ödeme Talebi</h2>
          {!showWithdrawalForm && (
            <button
              onClick={() => setShowWithdrawalForm(true)}
              disabled={!user?.availableBalance || user.availableBalance < 10}
              className="btn btn-success"
            >
              Ödeme Talep Et
            </button>
          )}
        </div>
        <div className="card-body">
          {showWithdrawalForm ? (
            <form
              onSubmit={handleSubmit(onSubmitWithdrawal)}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Çekim Tutarı
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="10"
                      max={user?.availableBalance || 0}
                      className={`form-input pl-8 ${errors.amount ? "error" : ""}`}
                      placeholder="0.00"
                      {...register("amount", {
                        required: "Tutar gereklidir",
                        min: { value: 10, message: "Minimum çekim tutarı $10" },
                        max: {
                          value: user?.availableBalance || 0,
                          message: `Maksimum çekim tutarı $${user?.availableBalance?.toFixed(2)}`,
                        },
                      })}
                    />
                  </div>
                  {errors.amount && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.amount.message}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Mevcut bakiye: ${user?.availableBalance?.toFixed(2)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ödeme Yöntemi
                  </label>
                  <select
                    className="form-select"
                    {...register("paymentMethod", {
                      required: "Ödeme yöntemi seçiniz",
                    })}
                  >
                    <option value="paypal">PayPal</option>
                    <option value="bank">Banka Havalesi</option>
                  </select>
                </div>
              </div>

              {paymentMethod === "paypal" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PayPal E-posta
                  </label>
                  <input
                    type="email"
                    className={`form-input ${errors.paypalEmail ? "error" : ""}`}
                    placeholder="paypal@example.com"
                    {...register("paypalEmail", {
                      required: "PayPal e-posta adresi gereklidir",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Geçerli bir e-posta adresi girin",
                      },
                    })}
                  />
                  {errors.paypalEmail && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.paypalEmail.message}
                    </p>
                  )}
                </div>
              )}

              {paymentMethod === "bank" && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Banka Adı
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.bankName ? "error" : ""}`}
                      placeholder="Örnek Bankası"
                      {...register("bankName", {
                        required: "Banka adı gereklidir",
                      })}
                    />
                    {errors.bankName && (
                      <p className="mt-1 text-sm text-error-600">
                        {errors.bankName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hesap Numarası
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.accountNumber ? "error" : ""}`}
                      placeholder="1234567890"
                      {...register("accountNumber", {
                        required: "Hesap numarası gereklidir",
                      })}
                    />
                    {errors.accountNumber && (
                      <p className="mt-1 text-sm text-error-600">
                        {errors.accountNumber.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      IBAN
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="TR123456789012345678901234"
                      {...register("iban")}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowWithdrawalForm(false)}
                  className="btn btn-secondary"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-success"
                >
                  {isSubmitting ? "Gönderiliyor..." : "Ödeme Talep Et"}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center py-6">
              <CurrencyDollarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">
                {user?.availableBalance >= 10
                  ? "Ödeme talebinde bulunmak için butona tıklayın"
                  : "Minimum çekim tutarı $10.00. Daha fazla kazanç elde edin."}
              </p>
              <p className="text-sm text-gray-500">
                Ödemeler genellikle 24-48 saat içinde işlenir
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Payment History */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Ödeme Geçmişi</h2>
        </div>
        <div className="card-body">
          {payments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table table-hover w-full">
                <thead>
                  <tr>
                    <th>Durum</th>
                    <th>Tutar</th>
                    <th>Yöntem</th>
                    <th>Talep Tarihi</th>
                    <th>İşlem Tarihi</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(payment.status)}
                          <span
                            className={`text-sm font-medium ${
                              payment.status === "completed"
                                ? "text-green-600"
                                : payment.status === "pending"
                                  ? "text-yellow-600"
                                  : payment.status === "processing"
                                    ? "text-blue-600"
                                    : "text-red-600"
                            }`}
                          >
                            {getStatusText(payment.status)}
                          </span>
                        </div>
                      </td>
                      <td className="font-medium">
                        ${payment.amount?.toFixed(2)}
                      </td>
                      <td className="capitalize">{payment.paymentMethod}</td>
                      <td>
                        {new Date(payment.requestedAt).toLocaleDateString(
                          "tr-TR"
                        )}
                      </td>
                      <td>
                        {payment.processedAt
                          ? new Date(payment.processedAt).toLocaleDateString(
                              "tr-TR"
                            )
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <BanknotesIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">
                Henüz ödeme talebinde bulunmamışsınız
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Earnings History */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">
            Kazanç Geçmişi
          </h2>
        </div>
        <div className="card-body">
          {earnings.length > 0 ? (
            <div className="space-y-3">
              {earnings.map((earning, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 text-xs font-medium">
                        {earning.countryCode}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {earning.link?.title || earning.link?.shortCode}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(earning.datetime).toLocaleDateString("tr-TR")}{" "}
                        -
                        {new Date(earning.datetime).toLocaleTimeString("tr-TR")}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-600">
                      +${earning.earnings?.toFixed(6)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ChartBarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Henüz kazanç elde etmemişsiniz</p>
              <p className="text-sm text-gray-500">
                Linklerinizi paylaşın ve kazanç elde etmeye başlayın
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card bg-blue-50 border-blue-200">
          <div className="card-body">
            <h3 className="font-semibold text-blue-900 mb-3">
              Ödeme Bilgileri
            </h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Minimum çekim tutarı: $10.00</li>
              <li>• Ödemeler 24-48 saat içinde işlenir</li>
              <li>• PayPal ödemeleri anında</li>
              <li>• Banka havaleleri 1-3 iş günü</li>
            </ul>
          </div>
        </div>

        <div className="card bg-green-50 border-green-200">
          <div className="card-body">
            <h3 className="font-semibold text-green-900 mb-3">
              Kazanç İpuçları
            </h3>
            <ul className="text-sm text-green-800 space-y-2">
              <li>• Sosyal medyada linklerinizi paylaşın</li>
              <li>• Yüksek CPM ülkelerden trafik çekin</li>
              <li>• Referans sistemi ile ek gelir elde edin</li>
              <li>• Kaliteli içerik linkleyin</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EarningsPage;
